import { db } from '../database/firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { apiGet, apiPost, useApiData } from './apiClient';

export const getPayments = async ({ last = 50 } = {}) => {
  if (useApiData) {
    const qs = new URLSearchParams();
    qs.set('perPage', String(last));
    qs.set('sort', 'createdate');
    qs.set('order', 'desc');
    const res = await apiGet(`payments?${qs.toString()}`);
    return res?.data ?? [];
  }

  const collectionRef = collection(db, 'payments');
  const q = query(collectionRef, orderBy('createdate', 'desc'), limit(last));
  const snap = await getDocs(q);
  const rows = [];
  snap?.forEach((docRef) => rows.push({ id: docRef.id, ...docRef.data() }));
  return rows;
};

const FALLBACK_PAYMENT_TYPES = [
  { value: 'event', label: 'Etkinlik Ödemesi' },
  { value: 'advertisement', label: 'Reklam Ödemesi' },
  { value: 'subscription', label: 'Abonelik Ödemesi' },
  { value: 'other', label: 'Diğer' },
];

export const getPaymentTypes = async () => {
  if (!useApiData) return FALLBACK_PAYMENT_TYPES;
  try {
    const rows = await apiGet('payments/payment-types');
    if (Array.isArray(rows) && rows.length > 0) return rows;
  } catch {
    // API kapaliysa panel yine calissin
  }
  return FALLBACK_PAYMENT_TYPES;
};

export const getPaymentById = async (id) => {
  if (!id) return null;
  if (useApiData) {
    return apiGet(`payments/${id}`);
  }
  const rows = await getPayments({ last: 500 });
  return rows.find((row) => row.id === id) ?? null;
};

/**
 * Garanti üzerinden ödeme iadesi.
 * - amount: tutar bazlı (tam veya kısmi) iade
 * - items: ürün bazlı iade [{ groupKey, productId, count? }]
 */
export const refundPayment = async ({
  paymentId,
  amount,
  items,
  reason,
  refundReasonMail,
  refundReasonSms,
  refundReasonPush,
}) => {
  if (!useApiData) {
    throw new Error('Refund is only available with API data mode.');
  }
  const body = {};
  if (Array.isArray(items) && items.length > 0) {
    body.items = items;
  } else if (amount != null && amount !== '') {
    body.amount = Number(amount);
  }
  if (refundReasonMail) body.refundReasonMail = refundReasonMail;
  if (refundReasonSms) body.refundReasonSms = refundReasonSms;
  if (refundReasonPush) body.refundReasonPush = refundReasonPush;
  if (reason && !refundReasonMail) body.reason = reason;
  return apiPost(`payments/${paymentId}/refund`, body);
};

/**
 * Event bazlı toplu iade.
 * - groupKey/productId verilmezse etkinliğin tüm aktif satışları tamamen iade edilir.
 * - groupKey/productId verilirse her satıştaki eşleşen ürün satırları iade edilir.
 */
export const refundEvent = async ({
  eventId,
  groupKey,
  productId,
  reason,
  refundReasonMail,
  refundReasonSms,
  refundReasonPush,
}) => {
  if (!useApiData) {
    throw new Error('Refund is only available with API data mode.');
  }
  const body = {};
  if (groupKey) body.groupKey = groupKey;
  if (productId) body.productId = productId;
  if (refundReasonMail) body.refundReasonMail = refundReasonMail;
  if (refundReasonSms) body.refundReasonSms = refundReasonSms;
  if (refundReasonPush) body.refundReasonPush = refundReasonPush;
  if (reason && !refundReasonMail) body.reason = reason;
  return apiPost(`payments/refund/event/${eventId}`, body);
};

/**
 * Ödeme kaydına iade notu kaydeder (iade yapmadan).
 * Aynı gün kısmi iade yapılamadığında "ertesi gün iade edilecek" notunu saklamak için.
 */
export const savePaymentRefundNote = async ({ paymentId, note, pendingRefund = true }) => {
  if (!useApiData) {
    throw new Error('Refund note is only available with API data mode.');
  }
  return apiPost(`payments/${paymentId}/refund-note`, {
    note: note ?? '',
    pendingRefund,
  });
};

/**
 * İadeyi ertesi güne planlar (otomatik iade). Hangi ürünlerin iade edileceğini
 * kaydeder; backend bir gün sonra otomatik gerçekleştirir.
 */
export const schedulePendingRefund = async ({ paymentId, items, reason }) => {
  if (!useApiData) {
    throw new Error('Scheduled refund is only available with API data mode.');
  }
  const body = {};
  if (Array.isArray(items) && items.length > 0) body.items = items;
  if (reason) body.reason = reason;
  return apiPost(`payments/${paymentId}/schedule-refund`, body);
};

export default {
  getPayments,
  getPaymentTypes,
  getPaymentById,
  refundPayment,
  refundEvent,
  savePaymentRefundNote,
  schedulePendingRefund,
};
